// Sprint 6 — Condition Builder visuale (DEBT-024).
// AND/OR annidati con leaf cards. 6 leaf type:
//   captured_eq, captured_contains, captured_compare, flag_present, language_is, emotional_state_is.

import { useState, type Dispatch, type SetStateAction } from 'react';
import type { ConditionClause, Conditions, FlowQuestion } from '@audiq/shared';

type Tree = Conditions;
type Leaf = ConditionClause;
type Node = Leaf | Tree;

interface Props {
  value: Tree;
  onChange: (next: Tree) => void;
  questions: FlowQuestion[];
  /** Lista flag_name disponibili (raccolti dai flags_to_trigger di tutte le questions) */
  availableFlags: Array<{ name: string; severity: string }>;
}

export function ConditionBuilder({ value, onChange, questions, availableFlags }: Props) {
  return (
    <div className="space-y-3">
      <p className="mono" style={{ fontSize: 11, color: 'var(--mute)' }}>
        Aggiungi condizioni atomiche oppure gruppi annidati AND/OR. AND vuoto = sempre vero.
      </p>
      <NodeEditor
        node={value}
        questions={questions}
        availableFlags={availableFlags}
        onChange={(next) => onChange(next as Tree)}
        depth={0}
      />
      <ConditionPreview node={value} questions={questions} />
    </div>
  );
}

function isTree(n: Node): n is Tree {
  return typeof n === 'object' && n !== null && 'op' in n && 'rules' in n;
}

interface NodeEditorProps {
  node: Node;
  questions: FlowQuestion[];
  availableFlags: Array<{ name: string; severity: string }>;
  onChange: (next: Node) => void;
  depth: number;
}

function NodeEditor({ node, questions, availableFlags, onChange, depth }: NodeEditorProps) {
  if (!isTree(node)) {
    return (
      <LeafEditor
        clause={node}
        questions={questions}
        availableFlags={availableFlags}
        onChange={(c) => onChange(c)}
      />
    );
  }
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: depth === 0 ? 'var(--paper)' : 'var(--soft)',
        padding: 10,
      }}
    >
      <div className="mb-2 flex items-center gap-2">
        <select
          value={node.op}
          onChange={(e) => onChange({ ...node, op: e.target.value as 'AND' | 'OR' })}
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <option value="AND">Tutte queste (AND)</option>
          <option value="OR">Almeno una (OR)</option>
        </select>
        <span className="mono text-xs" style={{ color: 'var(--mute)' }}>
          {node.rules.length} condizion{node.rules.length === 1 ? 'e' : 'i'}
        </span>
      </div>
      <ul className="space-y-2 ms-2">
        {node.rules.map((r, i) => (
          <li
            key={i}
            className="flex items-start gap-2"
            style={{
              borderInlineStart: '2px solid var(--line)',
              paddingInlineStart: 8,
            }}
          >
            <div className="flex-1">
              <NodeEditor
                node={r as Node}
                questions={questions}
                availableFlags={availableFlags}
                onChange={(child) => {
                  const next = { ...node, rules: [...node.rules] };
                  next.rules[i] = child;
                  onChange(next);
                }}
                depth={depth + 1}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const next = { ...node, rules: node.rules.filter((_, j) => j !== i) };
                onChange(next);
              }}
              aria-label="Elimina"
              className="px-1 text-lg"
              style={{ color: 'var(--mute)', lineHeight: 1 }}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() =>
            onChange({
              ...node,
              rules: [...node.rules, { type: 'captured_eq', field: '', value: '' } as Leaf],
            })
          }
          className="rounded-md px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', border: '1px solid var(--line-strong)' }}
        >
          + Condizione
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              ...node,
              rules: [...node.rules, { op: node.op === 'AND' ? 'OR' : 'AND', rules: [] } as Tree],
            })
          }
          className="rounded-md px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', border: '1px solid var(--line-strong)' }}
        >
          + Gruppo {node.op === 'AND' ? 'OR' : 'AND'}
        </button>
      </div>
    </div>
  );
}

interface LeafProps {
  clause: Leaf;
  questions: FlowQuestion[];
  availableFlags: Array<{ name: string; severity: string }>;
  onChange: (c: Leaf) => void;
}

const LEAF_TYPES: Array<{ value: Leaf['type']; label: string }> = [
  { value: 'captured_eq', label: 'Risposta = valore' },
  { value: 'captured_contains', label: 'Risposta in lista' },
  { value: 'captured_compare', label: 'Risposta numerica (< > =)' },
  { value: 'flag_present', label: 'Flag scattato' },
  { value: 'language_is', label: 'Lingua rilevata' },
  { value: 'emotional_state_is', label: 'Stato emotivo' },
];

const COMPARE_OPS: Array<{ value: string; label: string }> = [
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gte', label: '≥' },
  { value: 'gt', label: '>' },
];

const EMOTIONAL_STATES = ['calmo', 'preoccupato', 'spaventato', 'in_dolore', 'frustrato', 'neutro'];

function LeafEditor({ clause, questions, availableFlags, onChange }: LeafProps) {
  const setType = (type: Leaf['type']) => {
    // Reset campi non compatibili.
    onChange({ type } as Leaf);
  };

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 8,
        background: 'var(--paper)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
      }}
    >
      <select
        value={clause.type}
        onChange={(e) => setType(e.target.value as Leaf['type'])}
        className="rounded-md border px-2 py-1 text-sm"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        {LEAF_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>

      {(clause.type === 'captured_eq' ||
        clause.type === 'captured_contains' ||
        clause.type === 'captured_compare') && (
        <QuestionFieldSelect
          questions={questions}
          value={clause.field ?? ''}
          onChange={(field) => onChange({ ...clause, field })}
        />
      )}

      {clause.type === 'captured_compare' && (
        <select
          value={clause.op ?? 'eq'}
          onChange={(e) => onChange({ ...clause, op: e.target.value as Leaf['op'] })}
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          {COMPARE_OPS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}

      {(clause.type === 'captured_eq' || clause.type === 'captured_compare') && (
        <input
          type={clause.type === 'captured_compare' ? 'number' : 'text'}
          value={String(clause.value ?? '')}
          onChange={(e) =>
            onChange({
              ...clause,
              value: clause.type === 'captured_compare' ? Number(e.target.value) : e.target.value,
            })
          }
          placeholder="valore"
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)', minWidth: 100 }}
        />
      )}

      {clause.type === 'captured_contains' && (
        <input
          type="text"
          value={(clause.values ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...clause,
              values: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="valori separati da virgola"
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)', minWidth: 180 }}
        />
      )}

      {clause.type === 'flag_present' && (
        <>
          <select
            value={clause.flag ?? ''}
            onChange={(e) => onChange({ ...clause, flag: e.target.value })}
            className="rounded-md border px-2 py-1 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <option value="">— scegli flag —</option>
            {availableFlags.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name}
              </option>
            ))}
          </select>
          <select
            value={clause.severity ?? ''}
            onChange={(e) =>
              onChange({
                ...clause,
                severity: (e.target.value || undefined) as Leaf['severity'],
              })
            }
            className="rounded-md border px-2 py-1 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <option value="">qualsiasi severity</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </>
      )}

      {clause.type === 'language_is' && (
        <input
          type="text"
          value={clause.code ?? ''}
          onChange={(e) => onChange({ ...clause, code: e.target.value })}
          placeholder="es. it, en, ar"
          maxLength={6}
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)', width: 120 }}
        />
      )}

      {clause.type === 'emotional_state_is' && (
        <select
          value={clause.state ?? ''}
          onChange={(e) => onChange({ ...clause, state: e.target.value })}
          className="rounded-md border px-2 py-1 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <option value="">— stato —</option>
          {EMOTIONAL_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function QuestionFieldSelect({
  questions,
  value,
  onChange,
}: {
  questions: FlowQuestion[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border px-2 py-1 text-sm"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)', maxWidth: 220 }}
    >
      <option value="">— scegli domanda —</option>
      {questions.map((q) => {
        const key = q.data_semantic ?? q.id;
        const label = q.data_semantic
          ? `${q.data_semantic} · ${q.objective.slice(0, 40)}`
          : q.objective.slice(0, 50);
        return (
          <option key={q.id} value={key}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

// =====================================================================
// Anteprima testuale
// =====================================================================

function ConditionPreview({ node, questions }: { node: Node; questions: FlowQuestion[] }) {
  const text = renderNode(node, questions);
  return (
    <div
      className="mt-2 rounded-md p-2"
      style={{ background: 'var(--surface)', fontSize: 12, color: 'var(--mute)' }}
    >
      <span className="mono" style={{ marginInlineEnd: 4 }}>
        Anteprima:
      </span>
      <span style={{ color: 'var(--ink)' }}>{text || 'sempre vero'}</span>
    </div>
  );
}

function renderNode(n: Node, questions: FlowQuestion[]): string {
  if (isTree(n)) {
    if (n.rules.length === 0) return n.op === 'AND' ? 'sempre vero' : 'sempre falso';
    const parts = n.rules.map((r) => renderNode(r as Node, questions));
    return `(${parts.join(n.op === 'AND' ? ' E ' : ' OPPURE ')})`;
  }
  const c = n;
  const qLabel = (field?: string) => {
    if (!field) return '?';
    const q = questions.find((x) => x.data_semantic === field || x.id === field);
    return q?.data_semantic ?? q?.objective.slice(0, 30) ?? field;
  };
  switch (c.type) {
    case 'captured_eq':
      return `${qLabel(c.field)} = ${JSON.stringify(c.value ?? '')}`;
    case 'captured_contains':
      return `${qLabel(c.field)} ∈ [${(c.values ?? []).join(', ')}]`;
    case 'captured_compare':
      return `${qLabel(c.field)} ${c.op ?? '?'} ${c.value ?? ''}`;
    case 'flag_present':
      return `flag "${c.flag ?? '?'}"${c.severity ? ` ${c.severity}` : ''}`;
    case 'language_is':
      return `lingua = ${c.code ?? '?'}`;
    case 'emotional_state_is':
      return `emozione = ${c.state ?? '?'}`;
    default:
      return '?';
  }
}

// Helper riusabile per estrarre i flag disponibili dalle questions del flow.
export function extractAvailableFlags(
  questions: FlowQuestion[],
): Array<{ name: string; severity: string }> {
  const map = new Map<string, string>();
  for (const q of questions) {
    for (const f of q.flags_to_trigger ?? []) {
      const flagName = (f as { flag_name?: string }).flag_name;
      const severity = (f as { severity?: string }).severity ?? 'info';
      if (flagName) map.set(flagName, severity);
    }
  }
  return Array.from(map.entries()).map(([name, severity]) => ({ name, severity }));
}

// helper esportato per consumer che desiderano gestire setValue come state diretto
export function useConditionState<T extends Tree>(initial: T): [T, Dispatch<SetStateAction<T>>] {
  return useState<T>(initial);
}
